import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  useWindowDimensions,
  Modal,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { useAtom, useAtomValue } from 'jotai';
import { illustrationInputAtom } from './illustrationAtoms';
import { selectedCustomerAtom } from './customerAtoms';
import { Icon } from '../components/Icon';
import { NumpadInputModal } from '../components/NumpadInputModal';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { RIDERS } from '../data/riders';
import { illustrationRepository } from '../repositories';
import { generateUUID } from '../core/uuid';
import { calculateSAMinMax } from '../core/calculations';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M06ConfigureProductScreen'>;

const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseNumber = (str: string): number => {
  const cleanStr = str.replace(/\./g, '').replace(/,/g, '');
  const parsed = parseInt(cleanStr, 10);
  return isNaN(parsed) ? 0 : parsed;
};

export const M06ConfigureProductScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProp<RootStackParamList, 'M06ConfigureProductScreen'>>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024;

  const [illustrationInput, setIllustrationInput] = useAtom(illustrationInputAtom);
  const selectedCustomer = useAtomValue(selectedCustomerAtom);

  const customerName =
    route.params?.customerName ||
    illustrationInput.customerName ||
    selectedCustomer?.name ||
    'Bên mua HĐBH';

  const customerAge =
    route.params?.customerAge ||
    illustrationInput.customerAge ||
    selectedCustomer?.age ||
    35;

  const customerGender =
    route.params?.customerGender ||
    illustrationInput.customerGender ||
    selectedCustomer?.gender ||
    'Nam';

  const PRODUCT_OPTIONS = [
    { code: 'UL049 - Lựa chọn STBH không tự động gia tăng', label: 'UL049 - Lựa chọn STBH không tự động gia tăng' },
    { code: 'UL050 - Lựa chọn STBH không tự động gia tăng', label: 'UL050 - Lựa chọn STBH không tự động gia tăng' },
    { code: 'UL050 - Lựa chọn STBH tự động gia tăng', label: 'UL050 - Lựa chọn STBH tự động gia tăng' },
  ];

  const FREQ_OPTIONS = [
    { code: 'Năm', label: 'Năm' },
    { code: 'Nửa năm', label: 'Nửa năm' },
    { code: 'Quý', label: 'Quý' },
  ];

  const PLAN_OPTIONS = [
    { code: 'Cơ bản', label: 'Cơ bản' },
    { code: 'Nâng cao', label: 'Nâng cao' },
  ];

  const ILLUSTRATION_TERM_OPTIONS = [
    { code: 'A - 20 năm hợp đồng đầu tiên', label: 'A - 20 năm hợp đồng đầu tiên' },
    { code: 'B - Tới hết thời hạn hợp đồng', label: 'B - Tới hết thời hạn hợp đồng' },
  ];

  const RIDER_MODAL_OPTIONS = [
    { id: 'R1', label: 'BH Chăm sóc sức khỏe nâng cao - Sống Khỏe Mỗi Ngày 2024 - Phiên bản cải tiến' },
    { id: 'R3', label: 'BH Trợ cấp y tế - Dự Phòng Xanh' },
    { id: 'R2', label: 'BH Bệnh Lý Nghiêm Trọng - Lá Chắn Xanh' },
    { id: 'R4', label: 'BH Tử vong và thương tật do Tai Nạn - Hộ Vệ Xanh' },
    { id: 'R5', label: 'BH Tử vong hoặc Thương Tật Toàn Bộ Vĩnh Viễn - Bảo Thân Xanh' },
  ];

  // Reusable Numpad Modal State
  const [activeNumpadModal, setActiveNumpadModal] = useState<{
    key: 'sumAssured' | 'term' | 'estPremium' | 'topUp' | null;
    title: string;
    unit?: string;
    rangeText?: string;
    initialValue: string | number;
    formatType?: 'number' | 'integer';
    maxLength?: number;
  }>({
    key: null,
    title: '',
    initialValue: '',
  });

  // Modal Thêm QL Bảo Vệ State
  const [showAddRiderModal, setShowAddRiderModal] = useState(false);
  const [tempSelectedRiders, setTempSelectedRiders] = useState<string[]>([]);

  // Form Fields matching ePOS screenshot
  const [productCode, setProductCode] = useState('UL050 - Lựa chọn STBH tự động gia tăng');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showFreqDropdown, setShowFreqDropdown] = useState(false);
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const [showIllustrationTermDropdown, setShowIllustrationTermDropdown] = useState(false);
  const [creationDate] = useState('12/08/2026');
  const [sumAssured, setSumAssured] = useState(illustrationInput.sumAssured || '1.000.000.000');
  const [estPremium, setEstPremium] = useState(illustrationInput.estPremium || '20.000.000');
  const [freq, setFreq] = useState(illustrationInput.freq || 'Năm');
  const [insurancePlan, setInsurancePlan] = useState('Cơ bản');
  const [term, setTerm] = useState(illustrationInput.term || '20');
  const [topUpAmount, setTopUpAmount] = useState<number>(illustrationInput.topUpAmount || 0);
  const [illustrationTerm, setIllustrationTerm] = useState('A - 20 năm hợp đồng đầu tiên');

  // Dynamic Sum Assured (STBH) Min/Max Constraint based on Age & Premium
  const estPremiumNum = parseNumber(estPremium);
  const saConstraint = calculateSAMinMax(customerAge, estPremiumNum);

  // Auto-clamp sumAssured to valid range [minSA, maxSA] by default
  useEffect(() => {
    if (!saConstraint.minSA) return;
    const currentSA = parseNumber(sumAssured);
    if (!currentSA || currentSA < saConstraint.minSA) {
      setSumAssured(formatNumber(saConstraint.minSA));
    } else if (currentSA > saConstraint.maxSA) {
      setSumAssured(formatNumber(saConstraint.maxSA));
    }
  }, [saConstraint.minSA, saConstraint.maxSA]);

  // Advanced Settings & Substandard Risk Checkbox
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [substandardRisk, setSubstandardRisk] = useState(false);

  // Riders Selection
  const [selectedRiders, setSelectedRiders] = useState<string[]>(
    illustrationInput.selectedRiders || []
  );
  const [r1Package, setR1Package] = useState<'silver' | 'gold' | 'platinum'>(
    illustrationInput.r1Package || 'silver'
  );
  const [r5Mode, setR5Mode] = useState<'complimentary' | 'attached'>(
    illustrationInput.r5Mode || 'complimentary'
  );

  const toggleRider = (id: string) => {
    if (selectedRiders.includes(id)) {
      setSelectedRiders(selectedRiders.filter((r) => r !== id));
    } else {
      setSelectedRiders([...selectedRiders, id]);
    }
  };

  const handleGenerateQuote = () => {
    if (!customerName || customerName === 'Bên mua bảo hiểm chưa chọn') {
      Alert.alert('Chưa chọn khách hàng', 'Vui lòng quay lại màn hình chọn khách hàng để chọn Bên mua bảo hiểm.');
      return;
    }

    // Save updated configuration to Jotai Atom (Global SSOT)
    setIllustrationInput({
      ...illustrationInput,
      productId: productCode,
      customerName,
      customerAge,
      customerGender,
      sumAssured,
      estPremium,
      term,
      freq,
      selectedRiders,
      topUpAmount,
      r1Package,
      r5Mode,
    });

    const illustrationId = (route.params as any)?.illustrationId || generateUUID();
    const dateStr = new Date().toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    illustrationRepository.save({
      id: illustrationId,
      customerId: selectedCustomer?.id || 'KH-NHAP',
      proposalName: `Xanh Ước Mơ - Gói ${customerName}`,
      productCode: productCode || 'XUM',
      customerName,
      customerAge,
      customerGender,
      sumAssured,
      term: parseInt(term, 10) || 20,
      freq,
      premium: estPremium,
      stage: 'new',
      lastUpdated: new Date().toISOString(),
      expiryDate: 'Tự động gia hạn',
      selectedRiders,
      topUpAmount,
    });

    // Navigate to M07 Result Screen
    navigation.navigate('M07ResultScreen', {
      illustrationId,
      productId: productCode,
      customerName,
      customerAge,
      customerGender,
      sumAssured,
      estPremium,
      term,
      freq,
      selectedRiders,
      topUpAmount,
      r1Package,
      r5Mode,
    });
  };

  return (
    <View style={styles.container}>

      <View style={[styles.mainLayout, isDesktop ? styles.mainLayoutDesktop : styles.mainLayoutMobile]}>
        {/* Left Navigation Tree Sidebar (Matching Screenshot Left Column) */}
        <View style={isDesktop ? styles.leftTreeSidebarDesktop : styles.leftTreeSidebarMobile}>
          <TouchableOpacity
            style={styles.treeHeaderItem}
            onPress={() => navigation.navigate('M06SelectCustomerScreen')}
          >
            <Text style={styles.treeHeaderTitle}>Hồ sơ khách hàng</Text>
          </TouchableOpacity>

          <View style={styles.treeActiveCard}>
            <Text style={styles.treeActiveTitle}>Sản phẩm Bảo hiểm Liên kết chung Xanh Ước Mơ</Text>
            <Text style={styles.treeActiveSub}>Bên mua HĐBH: {customerName}</Text>
          </View>

          {RIDERS.filter((r) => selectedRiders.includes(r.id)).map((rider) => (
            <View key={rider.id} style={styles.treeRiderItem}>
              <Text style={styles.treeRiderTitle}>{rider.name}</Text>
            </View>
          ))}

          <TouchableOpacity
            style={styles.sidebarAddRiderBtn}
            onPress={() => {
              setTempSelectedRiders([...selectedRiders]);
              setShowAddRiderModal(true);
            }}
          >
            <Icon name="add_circle" size={20} color="#059669" />
            <Text style={styles.sidebarAddRiderBtnText}>Thêm QL Bảo Vệ</Text>
          </TouchableOpacity>
        </View>

        {/* Right Configuration Form Area (Matching Screenshot Form) */}
        <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.formContainerContent}>
          {/* Header Gap Amount Banner */}
          <View style={styles.headerGapCard}>
            <View>
              <Text style={styles.gapTitleText}>Số Tiền Bảo Vệ Thiếu Hụt</Text>
              <Text style={styles.gapAmountText}>{sumAssured}</Text>
            </View>
            <Text style={styles.gapSubtext}>Chưa phân tích nhu cầu</Text>
          </View>

          {/* Form Fields Grid */}
          <View style={styles.formGrid}>
            {/* Row 1 */}
            <View style={[styles.formRow, { zIndex: 100 }]}>
              <View style={[styles.formGroupCol, { zIndex: 10 }]}>
                <Text style={styles.label}>
                  Mã sản phẩm <Text style={styles.requiredStar}>*</Text>
                </Text>
                <TouchableOpacity
                  style={styles.selectInputBox}
                  onPress={() => setShowProductDropdown(!showProductDropdown)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.selectInputText}>{productCode}</Text>
                  <Icon name="expand_more" size={20} color={colors.onSurfaceVariant} />
                </TouchableOpacity>

                {showProductDropdown && (
                  <View style={styles.dropdownMenu}>
                    {PRODUCT_OPTIONS.map((opt) => (
                      <TouchableOpacity
                        key={opt.code}
                        style={[
                          styles.dropdownItem,
                          productCode === opt.code && styles.dropdownItemActive,
                        ]}
                        onPress={() => {
                          setProductCode(opt.code);
                          setShowProductDropdown(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.dropdownItemText,
                            productCode === opt.code && styles.dropdownItemTextActive,
                          ]}
                        >
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>

              <View style={styles.formGroupCol}>
                <Text style={styles.label}>Ngày tạo TLNHH</Text>
                <TextInput style={[styles.input, styles.readOnlyInput]} value={creationDate} editable={false} />
              </View>
            </View>

            {/* Row 2 */}
            <View style={styles.formRow}>
              <View style={styles.formGroupCol}>
                <Text style={styles.label}>
                  Số Tiền Bảo Vệ Cơ Bản <Text style={styles.requiredStar}>*</Text>
                </Text>
                <TouchableOpacity
                  style={styles.selectInputBox}
                  onPress={() =>
                    setActiveNumpadModal({
                      key: 'sumAssured',
                      title: 'Số Tiền Bảo Vệ Cơ Bản',
                      unit: 'Đơn vị: VNĐ',
                      rangeText: `Hạn mức: Từ ${formatNumber(saConstraint.minSA)} VNĐ đến ${formatNumber(saConstraint.maxSA)} VNĐ (${saConstraint.minMultiplier}x đến ${saConstraint.maxMultiplier}x phí)`,
                      initialValue: sumAssured,
                      formatType: 'number',
                    })
                  }
                  activeOpacity={0.7}
                >
                  <Text style={styles.selectInputText}>{sumAssured}</Text>
                  <Icon name="edit" size={18} color="#059669" />
                </TouchableOpacity>
              </View>

              <View style={styles.formGroupCol}>
                <Text style={styles.label}>Phí BH Cơ Bản</Text>
                <TextInput
                  style={styles.input}
                  value={estPremium}
                  onChangeText={setEstPremium}
                  keyboardType="numeric"
                  placeholder="20.000.000"
                />
              </View>
            </View>

            {/* Row 3 */}
            <View style={[styles.formRow, { zIndex: 90 }]}>
              <View style={[styles.formGroupCol, { zIndex: 10 }]}>
                <Text style={styles.label}>
                  Định kỳ đóng phí <Text style={styles.requiredStar}>*</Text>
                </Text>
                <TouchableOpacity
                  style={styles.selectInputBox}
                  onPress={() => setShowFreqDropdown(!showFreqDropdown)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.selectInputText}>{freq}</Text>
                  <Icon name="expand_more" size={20} color={colors.onSurfaceVariant} />
                </TouchableOpacity>

                {showFreqDropdown && (
                  <View style={styles.dropdownMenu}>
                    {FREQ_OPTIONS.map((opt) => (
                      <TouchableOpacity
                        key={opt.code}
                        style={[
                          styles.dropdownItem,
                          freq === opt.code && styles.dropdownItemActive,
                        ]}
                        onPress={() => {
                          setFreq(opt.code);
                          setShowFreqDropdown(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.dropdownItemText,
                            freq === opt.code && styles.dropdownItemTextActive,
                          ]}
                        >
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>

              <View style={[styles.formGroupCol, { zIndex: 10 }]}>
                <Text style={styles.label}>Kế Hoạch Bảo Hiểm</Text>
                <TouchableOpacity
                  style={styles.selectInputBox}
                  onPress={() => setShowPlanDropdown(!showPlanDropdown)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.selectInputText}>{insurancePlan}</Text>
                  <Icon name="expand_more" size={20} color={colors.onSurfaceVariant} />
                </TouchableOpacity>

                {showPlanDropdown && (
                  <View style={styles.dropdownMenu}>
                    {PLAN_OPTIONS.map((opt) => (
                      <TouchableOpacity
                        key={opt.code}
                        style={[
                          styles.dropdownItem,
                          insurancePlan === opt.code && styles.dropdownItemActive,
                        ]}
                        onPress={() => {
                          setInsurancePlan(opt.code);
                          setShowPlanDropdown(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.dropdownItemText,
                            insurancePlan === opt.code && styles.dropdownItemTextActive,
                          ]}
                        >
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            </View>

            {/* Row 4 */}
            <View style={styles.formRow}>
              <View style={styles.formGroupCol}>
                <Text style={styles.label}>Thời Hạn Đóng Phí</Text>
                <TouchableOpacity
                  style={styles.selectInputBox}
                  onPress={() =>
                    setActiveNumpadModal({
                      key: 'term',
                      title: 'Thời Hạn Đóng Phí',
                      rangeText: 'Từ 3 đến 63',
                      initialValue: term,
                      formatType: 'integer',
                      maxLength: 3,
                    })
                  }
                  activeOpacity={0.7}
                >
                  <Text style={styles.selectInputText}>{term}</Text>
                  <Icon name="edit" size={18} color="#059669" />
                </TouchableOpacity>
              </View>

              <View style={styles.formGroupCol}>
                <Text style={styles.label}>Phí Đóng Thêm Hàng Năm</Text>
                <TouchableOpacity
                  style={styles.selectInputBox}
                  onPress={() =>
                    setActiveNumpadModal({
                      key: 'topUp',
                      title: 'Phí Đóng Thêm Hàng Năm',
                      unit: 'Đơn vị: VNĐ',
                      rangeText: 'Từ 0 VNĐ đến 500.000.000 VNĐ',
                      initialValue: topUpAmount,
                      formatType: 'number',
                    })
                  }
                  activeOpacity={0.7}
                >
                  <Text style={styles.selectInputText}>
                    {topUpAmount === 0 ? '0' : formatNumber(topUpAmount)}
                  </Text>
                  <Icon name="edit" size={18} color="#059669" />
                </TouchableOpacity>
              </View>
            </View>

            {/* Row 5 */}
            <View style={[styles.formRow, { zIndex: 80 }]}>
              <View style={[styles.formGroupCol, { zIndex: 10 }]}>
                <Text style={styles.label}>Thời Hạn Minh Họa</Text>
                <TouchableOpacity
                  style={styles.selectInputBox}
                  onPress={() => setShowIllustrationTermDropdown(!showIllustrationTermDropdown)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.selectInputText}>{illustrationTerm}</Text>
                  <Icon name="expand_more" size={20} color={colors.onSurfaceVariant} />
                </TouchableOpacity>

                {showIllustrationTermDropdown && (
                  <View style={styles.dropdownMenu}>
                    {ILLUSTRATION_TERM_OPTIONS.map((opt) => (
                      <TouchableOpacity
                        key={opt.code}
                        style={[
                          styles.dropdownItem,
                          illustrationTerm === opt.code && styles.dropdownItemActive,
                        ]}
                        onPress={() => {
                          setIllustrationTerm(opt.code);
                          setShowIllustrationTermDropdown(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.dropdownItemText,
                            illustrationTerm === opt.code && styles.dropdownItemTextActive,
                          ]}
                        >
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
              <View style={styles.formGroupCol} />
            </View>
          </View>

          {/* Advanced Settings Section (Collapsible - Matching Screenshot) */}
          <View style={styles.advancedSection}>
            <TouchableOpacity
              style={styles.advancedHeaderRow}
              onPress={() => setShowAdvanced(!showAdvanced)}
              activeOpacity={0.7}
            >
              <Icon name={showAdvanced ? 'expand_less' : 'expand_more'} size={20} color="#64748b" />
              <Text style={styles.advancedHeaderTitle}>Cài đặt nâng cao</Text>
            </TouchableOpacity>

            {showAdvanced && (
              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => setSubstandardRisk(!substandardRisk)}
                activeOpacity={0.8}
              >
                <Icon
                  name={substandardRisk ? 'check_box' : 'check_box_outline_blank'}
                  size={20}
                  color={substandardRisk ? colors.primary : '#94a3b8'}
                />
                <Text style={styles.checkboxTextLabel}>Chi tiêu rủi ro dưới chuẩn</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Riders Section */}
          <View style={styles.ridersSection}>
            <View style={styles.ridersSectionHeader}>
              <Text style={styles.sectionTitleText}>Sản Phẩm Bổ Trợ</Text>
              <TouchableOpacity
                style={styles.addRiderBtn}
                onPress={() => {
                  setTempSelectedRiders([...selectedRiders]);
                  setShowAddRiderModal(true);
                }}
                activeOpacity={0.8}
              >
                <Icon name="add" size={18} color="#059669" />
                <Text style={styles.addRiderBtnText}>Thêm QL Bảo Vệ</Text>
              </TouchableOpacity>
            </View>

            {/* List of selected riders */}
            {selectedRiders.length === 0 ? (
              <Text style={styles.emptyRidersText}>Chưa có sản phẩm bổ trợ nào được chọn</Text>
            ) : (
              <View style={styles.selectedRidersList}>
                {selectedRiders.map((rId) => {
                  const riderInfo = RIDER_MODAL_OPTIONS.find((r) => r.id === rId);
                  return (
                    <View key={rId} style={styles.selectedRiderChip}>
                      <Icon name="check_circle" size={18} color="#059669" />
                      <Text style={styles.selectedRiderChipText} numberOfLines={2}>
                        {riderInfo?.label || rId}
                      </Text>
                      <TouchableOpacity
                        onPress={() => setSelectedRiders(selectedRiders.filter((id) => id !== rId))}
                        style={{ padding: 4 }}
                      >
                        <Icon name="close" size={16} color="#94a3b8" />
                      </TouchableOpacity>
                    </View>
                  );
                })}
              </View>
            )}
          </View>

          {/* Footer Submit Button */}
          <View style={styles.footerSubmitRow}>
            <TouchableOpacity style={styles.submitBtn} onPress={handleGenerateQuote}>
              <Text style={styles.submitBtnText}>TIẾP TỤC: XEM BẢN MINH HỌA & DÒNG TIỀN</Text>
              <Icon name="arrow_forward" size={18} color="#ffffff" />
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      {/* Reusable Numpad Input Modal Base */}
      <NumpadInputModal
        visible={activeNumpadModal.key !== null}
        title={activeNumpadModal.title}
        unit={activeNumpadModal.unit}
        rangeText={activeNumpadModal.rangeText}
        initialValue={activeNumpadModal.initialValue}
        formatType={activeNumpadModal.formatType}
        maxLength={activeNumpadModal.maxLength}
        onConfirm={(val) => {
          if (activeNumpadModal.key === 'sumAssured') {
            const valNum = parseNumber(val);
            if (valNum < saConstraint.minSA) {
              setSumAssured(formatNumber(saConstraint.minSA));
              Alert.alert(
                'Ràng buộc STBH',
                `Số tiền bảo hiểm tối thiểu là ${formatNumber(saConstraint.minSA)} VNĐ cho mức phí cơ bản hiện tại (${formatNumber(estPremiumNum)} VNĐ).`
              );
            } else if (valNum > saConstraint.maxSA) {
              setSumAssured(formatNumber(saConstraint.maxSA));
              Alert.alert(
                'Ràng buộc STBH',
                `Số tiền bảo hiểm tối đa là ${formatNumber(saConstraint.maxSA)} VNĐ (${saConstraint.maxMultiplier}x phí cơ bản) cho tuổi ${customerAge}.`
              );
            } else {
              setSumAssured(val);
            }
          } else if (activeNumpadModal.key === 'term') {
            setTerm(val);
          } else if (activeNumpadModal.key === 'topUp') {
            setTopUpAmount(parseNumber(val));
          } else if (activeNumpadModal.key === 'estPremium') {
            setEstPremium(val);
          }
        }}
        onClose={() => setActiveNumpadModal({ key: null, title: '', initialValue: '' })}
      />

      {/* Modal Popup Thêm QL Bảo Vệ matching user screenshot */}
      <Modal
        visible={showAddRiderModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowAddRiderModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowAddRiderModal(false)}
        >
          <TouchableOpacity
            style={styles.addRiderModalCard}
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <View style={styles.modalHeaderRow}>
              <Text style={styles.addRiderModalTitle}>Thêm QL Bảo Vệ</Text>
              <TouchableOpacity onPress={() => setShowAddRiderModal(false)} style={styles.closeBtn}>
                <Icon name="close" size={20} color="#334155" />
              </TouchableOpacity>
            </View>

            {/* Rider Options List */}
            <ScrollView style={styles.addRiderListScroll}>
              {RIDER_MODAL_OPTIONS.map((item) => {
                const isSelected = tempSelectedRiders.includes(item.id);
                return (
                  <TouchableOpacity
                    key={item.id}
                    style={styles.riderOptionRow}
                    onPress={() => {
                      if (isSelected) {
                        setTempSelectedRiders(tempSelectedRiders.filter((id) => id !== item.id));
                      } else {
                        setTempSelectedRiders([...tempSelectedRiders, item.id]);
                      }
                    }}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.radioCircle, isSelected && styles.radioCircleSelected]}>
                      {isSelected && <View style={styles.radioDot} />}
                    </View>
                    <Text style={styles.riderOptionLabel}>{item.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>

            {/* Modal Footer Button */}
            <View style={styles.addRiderFooter}>
              <TouchableOpacity
                style={[
                  styles.addRiderConfirmBtn,
                  tempSelectedRiders.length === 0 && styles.addRiderConfirmBtnDisabled,
                ]}
                onPress={() => {
                  setSelectedRiders(tempSelectedRiders);
                  setShowAddRiderModal(false);
                }}
              >
                <Text style={styles.addRiderConfirmBtnText}>Thêm</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  topBreadcrumbBar: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2],
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  breadcrumbText: {
    fontSize: 12,
    color: '#64748b',
  },
  breadcrumbLink: {
    color: colors.primary,
    fontWeight: fontWeights.semibold,
  },
  breadcrumbActive: {
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },
  mainLayout: {
    flex: 1,
  },
  mainLayoutDesktop: {
    flexDirection: 'row',
  },
  mainLayoutMobile: {
    flexDirection: 'column',
  },
  /* Left Tree Sidebar (Matching Screenshot Left Column) */
  leftTreeSidebarDesktop: {
    width: 280,
    backgroundColor: '#f8fafc',
    borderRightWidth: 1,
    borderRightColor: '#e2e8f0',
    padding: spacing[3],
  },
  leftTreeSidebarMobile: {
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    padding: spacing[3],
  },
  treeHeaderItem: {
    paddingVertical: spacing[2],
    marginBottom: spacing[2],
  },
  treeHeaderTitle: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: '#334155',
  },
  treeActiveCard: {
    backgroundColor: '#ffffff',
    padding: spacing[3],
    borderRadius: borderRadius.md,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    marginBottom: spacing[2],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  treeActiveTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
    marginBottom: 4,
  },
  treeActiveSub: {
    fontSize: 11,
    color: '#64748b',
  },
  treeRiderItem: {
    backgroundColor: '#ffffff',
    padding: spacing[2.5],
    borderRadius: borderRadius.sm,
    marginBottom: spacing[1.5],
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  treeRiderTitle: {
    fontSize: 12,
    color: '#334155',
  },
  sidebarAddRiderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    paddingVertical: spacing[2.5],
    marginTop: spacing[2],
  },
  sidebarAddRiderBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },

  /* Right Form Area (Matching Screenshot Right Form) */
  formContainerContent: {
    padding: spacing[5],
    paddingBottom: spacing[8],
  },
  headerGapCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 2,
    borderBottomColor: '#86efac',
    paddingBottom: spacing[3],
    marginBottom: spacing[5],
  },
  gapTitleText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: '#475569',
    marginBottom: 4,
  },
  gapAmountText: {
    fontSize: 32,
    fontWeight: '300',
    color: '#0f172a',
  },
  gapSubtext: {
    fontSize: 12,
    color: '#94a3b8',
  },
  formGrid: {
    gap: spacing[4],
    marginBottom: spacing[5],
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing[4],
  },
  formGroupCol: {
    flex: 1,
  },
  label: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: '#475569',
    marginBottom: 6,
  },
  requiredStar: {
    color: colors.primary,
  },
  input: {
    height: 40,
    backgroundColor: '#ffffff',
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: '#64748b',
    fontSize: 14,
    color: '#0f172a',
    paddingHorizontal: 0,
  },
  readOnlyInput: {
    borderBottomColor: '#cbd5e1',
    color: '#94a3b8',
  },
  selectInputBox: {
    height: 42,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#059669',
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[3],
  },
  selectInputText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
    flex: 1,
  },
  /* Advanced Settings Collapsible */
  advancedSection: {
    marginBottom: spacing[5],
    paddingTop: spacing[2],
  },
  advancedHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: spacing[2],
  },
  advancedHeaderTitle: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: '#475569',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    marginTop: spacing[2],
  },
  checkboxTextLabel: {
    fontSize: 13,
    color: '#334155',
  },
  /* Modal Overlay Style */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
  },
  /* Riders Section */
  ridersSection: {
    marginBottom: spacing[5],
  },
  sectionTitleText: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },
  ridersSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[3],
  },
  addRiderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
    borderWidth: 1.5,
    borderColor: '#059669',
    borderRadius: borderRadius.sm,
    backgroundColor: '#ffffff',
  },
  addRiderBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#059669',
  },
  emptyRidersText: {
    fontSize: 13,
    color: '#94a3b8',
    fontStyle: 'italic',
    paddingVertical: spacing[2],
  },
  selectedRidersList: {
    gap: spacing[2],
  },
  selectedRiderChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    backgroundColor: '#f0fdf4',
    borderWidth: 1,
    borderColor: '#a7f3d0',
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2.5],
  },
  selectedRiderChipText: {
    flex: 1,
    fontSize: 13,
    fontWeight: fontWeights.medium,
    color: '#065f46',
  },

  /* Add Rider Modal Popup Styles matching user screenshot */
  addRiderModalCard: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    maxHeight: '80%',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 12,
  },
  modalHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: spacing[3],
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    marginBottom: spacing[2],
  },
  addRiderModalTitle: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },
  closeBtn: {
    padding: 4,
  },
  addRiderListScroll: {
    maxHeight: 320,
  },
  riderOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: spacing[3],
    paddingHorizontal: spacing[2],
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  radioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: '#cbd5e1',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  radioCircleSelected: {
    borderColor: '#059669',
  },
  radioDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#059669',
  },
  riderOptionLabel: {
    flex: 1,
    fontSize: 13.5,
    color: '#1e293b',
    lineHeight: 19,
  },
  footerSubmitRow: {
    marginTop: spacing[4],
  },
  submitBtn: {
    backgroundColor: colors.primary,
    height: 48,
    borderRadius: borderRadius.md,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing[2],
  },
  submitBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
  dropdownMenu: {
    position: 'absolute',
    top: 70,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    borderWidth: 1.5,
    borderColor: '#059669',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 9999,
    zIndex: 9999,
    overflow: 'hidden',
  },
  dropdownItem: {
    backgroundColor: '#ffffff',
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[3],
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  dropdownItemActive: {
    backgroundColor: '#ecfdf5',
  },
  dropdownItemText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: '#0f172a',
  },
  dropdownItemTextActive: {
    color: '#059669',
    fontWeight: fontWeights.bold,
  },
  addRiderFooter: {
    paddingTop: spacing[4],
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
    alignItems: 'flex-start',
  },
  addRiderConfirmBtn: {
    backgroundColor: '#059669',
    paddingHorizontal: spacing[8],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.sm,
  },
  addRiderConfirmBtnDisabled: {
    backgroundColor: '#cbd5e1',
  },
  addRiderConfirmBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
});
